'use client'

import { useEffect, useRef, useState } from 'react'

// Searchable document picker for the Z2H admin panels — replaces the manual
// "paste the ID" inputs. Type a founder, venture, or email (or a raw ID) and
// pick from live results out of the Payload REST API; focusing the empty
// field lists recent documents so common picks are one click. Selection is
// the document ID; free text alone never submits.
//
// props:
//   collection    — Payload collection slug to search
//   placeholder   — input placeholder
//   value         — selected doc id (or '')
//   onChange(id, doc) — '' when cleared
//   initialWhere  — extra where-clauses (object of query params) for the
//                   empty-query listing, e.g. recent approved applications
//   statusColor(status) — optional CSS color for the status badge
//   fieldStyle    — base input style from the host panel
const SEARCH_FIELDS = ['founderName', 'ventureName', 'email']

function buildQuery(q, initialWhere) {
  const params = new URLSearchParams({ limit: '8', depth: '0', sort: '-createdAt' })
  const trimmed = q.trim()
  if (trimmed) {
    SEARCH_FIELDS.forEach((f, i) => {
      params.set(`where[or][${i}][${f}][like]`, trimmed)
    })
    if (/^\d+$/.test(trimmed)) {
      params.set(`where[or][${SEARCH_FIELDS.length}][id][equals]`, trimmed)
    }
  } else if (initialWhere) {
    for (const [k, v] of Object.entries(initialWhere)) params.set(k, v)
  }
  return params.toString()
}

function docLabel(doc) {
  const who = doc.founderName || doc.email || `#${doc.id}`
  return doc.ventureName ? `${who} — ${doc.ventureName}` : who
}

export function DocSearchSelect({
  collection,
  placeholder,
  value,
  onChange,
  initialWhere,
  statusColor,
  fieldStyle,
}) {
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(null)
  const [options, setOptions] = useState([])
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const [loading, setLoading] = useState(false)
  const rootRef = useRef(null)
  const fetchSeq = useRef(0)

  // Parent cleared the value (e.g. after a successful submit) → reset.
  useEffect(() => {
    if (!value) {
      setSelected(null)
      setQuery('')
    }
  }, [value])

  useEffect(() => {
    if (!open) return
    const seq = ++fetchSeq.current
    setLoading(true)
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `/payload-api/${collection}?${buildQuery(query, initialWhere)}`,
          { credentials: 'include' },
        )
        const data = await res.json()
        if (seq === fetchSeq.current) {
          setOptions(Array.isArray(data.docs) ? data.docs : [])
          setHighlight(0)
        }
      } catch {
        if (seq === fetchSeq.current) setOptions([])
      } finally {
        if (seq === fetchSeq.current) setLoading(false)
      }
    }, query.trim() ? 250 : 0)
    return () => clearTimeout(t)
  }, [query, open, collection, initialWhere])

  // Close on outside click.
  useEffect(() => {
    function onDocMouseDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [])

  function select(doc) {
    setSelected(doc)
    setQuery('')
    setOpen(false)
    onChange(String(doc.id), doc)
  }

  function clear() {
    setSelected(null)
    setQuery('')
    onChange('', null)
  }

  function onKeyDown(e) {
    if (!open) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((h) => Math.min(h + 1, options.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      // Selection, not form submit, while the list is open.
      e.preventDefault()
      if (options[highlight]) select(options[highlight])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div ref={rootRef} style={{ position: 'relative', flex: '1 1 260px', minWidth: 0 }}>
      {selected ? (
        <div
          style={{
            ...fieldStyle,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {docLabel(selected)}{' '}
            <span style={{ color: 'var(--theme-elevation-500)' }}>#{selected.id}</span>
          </span>
          <button
            type="button"
            onClick={clear}
            aria-label="Clear selection"
            style={{
              marginLeft: 'auto',
              border: 'none',
              background: 'none',
              color: 'var(--theme-elevation-500)',
              cursor: 'pointer',
              fontSize: 15,
              lineHeight: 1,
              padding: 2,
            }}
          >
            ×
          </button>
        </div>
      ) : (
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          style={{ ...fieldStyle, width: '100%', boxSizing: 'border-box' }}
        />
      )}
      {open && !selected && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 20,
            margin: 0,
            padding: 4,
            listStyle: 'none',
            maxHeight: 280,
            overflowY: 'auto',
            borderRadius: 8,
            border: '1px solid var(--theme-elevation-150)',
            background: 'var(--theme-input-bg, var(--theme-elevation-0))',
            boxShadow: '0 8px 24px rgba(0,0,0,.12)',
          }}
        >
          {loading && options.length === 0 ? (
            <li style={{ padding: '10px 12px', fontSize: 13, color: 'var(--theme-elevation-500)' }}>
              Searching…
            </li>
          ) : options.length === 0 ? (
            <li style={{ padding: '10px 12px', fontSize: 13, color: 'var(--theme-elevation-500)' }}>
              {query.trim() ? 'No matches.' : 'Nothing here yet.'}
            </li>
          ) : (
            options.map((doc, i) => (
              <li key={doc.id} role="option" aria-selected={i === highlight}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => select(doc)}
                  onMouseEnter={() => setHighlight(i)}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 8,
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 13,
                    color: 'var(--theme-text)',
                    background: i === highlight ? 'var(--theme-elevation-100)' : 'transparent',
                  }}
                >
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {docLabel(doc)}
                  </span>
                  <span style={{ marginLeft: 'auto', flexShrink: 0, fontSize: 12, color: 'var(--theme-elevation-500)' }}>
                    #{doc.id}
                  </span>
                  {doc.status ? (
                    <span
                      style={{
                        flexShrink: 0,
                        fontSize: 11,
                        textTransform: 'capitalize',
                        color: statusColor ? statusColor(doc.status) : 'var(--theme-elevation-500)',
                      }}
                    >
                      {String(doc.status).replace(/-/g, ' ')}
                    </span>
                  ) : null}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}
